import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logoGala from '../assets/common/LogoGala.png';
import FormInput from '../components/FormInput';
import OtpInput from '../components/OtpInput';
import { apiForgotPassword, apiResetPassword } from '../services/api';
import { PASSWORD_RULES_ATTR, PWD_RULES, validatePassword } from '../utils/password';
import { validateEmail } from '../utils/signupValidation';
import { clearEmailCodeLink, readEmailCodeLink } from '../utils/emailCodeLink';
import './auth.css';

const ForgotPassword = () => {
  const navigate = useNavigate();
  // Bouton de l'e-mail : l'adresse et le code arrivent déjà saisis, il ne reste
  // qu'à choisir le mot de passe (jamais validé d'office : il faut le taper).
  const [link] = useState(() => {
    const l = readEmailCodeLink();
    return l.mail && l.code ? l : null;
  });
  const [step, setStep] = useState(link ? 'reset' : 'email'); // 'email' | 'reset' | 'done'
  const [email, setEmail] = useState(link?.mail || '');
  const [code, setCode] = useState(link?.code || '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { clearEmailCodeLink(); }, []);

  const pwdChecks = validatePassword(password);
  const allPwdValid = Object.values(pwdChecks).every(Boolean);
  const confirmMismatch = confirm.length > 0 && confirm !== password;

  const handleRequestCode = async (e) => {
    e?.preventDefault();
    setError('');
    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      return;
    }
    try {
      await apiForgotPassword(email.trim());
      // Réponse neutre côté backend (anti-énumération) : on passe à l'étape suivante
      // quoi qu'il arrive.
      setStep('reset');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReset = async (e) => {
    e?.preventDefault();
    setError('');
    if (code.length !== 6) {
      setError('Saisissez les 6 chiffres du code reçu par email.');
      return;
    }
    if (!allPwdValid) {
      setError('Le mot de passe ne respecte pas les critères.');
      return;
    }
    if (confirm !== password) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    try {
      await apiResetPassword(email.trim(), code, password);
      setStep('done');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="page">
      <div className="container">
        <div className="signup-logo">
          <img src={logoGala} alt="CycleGala" className="signup-logo-image" />
        </div>

        <h2 className="signup-brand">
          <span className="signup-brand--cycle">Cycle</span>
          <span className="signup-brand--gala">Gala</span>
        </h2>

        {step === 'email' && (
          <form onSubmit={handleRequestCode} noValidate>
            <p className="subtitle">
              Entrez votre email : nous vous enverrons un code pour réinitialiser votre mot de passe.
            </p>

            <FormInput
              label="Email"
              type="email"
              name="email"
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="votre.email@exemple.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            {error && <p className="auth-error">{error}</p>}

            <div className="button-group">
              <button type="submit" className="btn btn--primary">
                Envoyer le code
              </button>
            </div>

            <div className="auth-link-row">
              <button type="button" className="auth-link-btn" onClick={() => navigate('/connexion')}>
                ‹ Retour à la connexion
              </button>
            </div>
          </form>
        )}

        {step === 'reset' && (
          <form onSubmit={handleReset} noValidate>
            <p className="subtitle">
              {link && code === link.code
                ? "Code repris de l'e-mail : il ne vous reste qu'à choisir un nouveau mot de passe."
                : `Si un compte existe pour ${email}, un code à 6 chiffres vient d'être envoyé. `
                  + 'Saisissez-le puis choisissez un nouveau mot de passe.'}
            </p>

            {/* Identifiant caché : sans lui, le gestionnaire de mots de passe ne sait
                pas à quel compte rattacher le nouveau mot de passe qu'il enregistre. */}
            <input
              type="email"
              name="email"
              autoComplete="username"
              value={email.trim()}
              readOnly
              hidden
            />

            <OtpInput
              label="Code de réinitialisation"
              idPrefix="reset-otp"
              autoFocus={!link}
              value={code}
              onChange={(v) => { setCode(v); setError(''); }}
            />

            <FormInput
              label="Nouveau mot de passe"
              type="password"
              name="new-password"
              autoComplete="new-password"
              passwordrules={PASSWORD_RULES_ATTR}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {password.length > 0 && (
              <ul className="pwd-checklist">
                {PWD_RULES.map(({ key, label }) => (
                  <li key={key} className={`pwd-checklist__item${pwdChecks[key] ? ' pwd-checklist__item--ok' : ''}`}>
                    {pwdChecks[key] ? '✓' : '·'} {label}
                  </li>
                ))}
              </ul>
            )}

            <FormInput
              label="Confirmer le mot de passe"
              type="password"
              name="confirm-password"
              autoComplete="new-password"
              placeholder="••••••••"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {confirmMismatch && <p className="auth-error">Les mots de passe ne correspondent pas.</p>}

            {error && <p className="auth-error">{error}</p>}

            <div className="button-group">
              <button type="submit" className="btn btn--primary">
                Réinitialiser
              </button>
            </div>

            <div className="auth-link-row">
              <button type="button" className="auth-link-btn" onClick={() => { setStep('email'); setError(''); setCode(''); }}>
                ‹ Modifier l'email
              </button>
            </div>
          </form>
        )}

        {step === 'done' && (
          <>
            <div style={{ fontSize: '48px', textAlign: 'center', margin: '8px 0' }}>✅</div>
            <p className="subtitle">
              Votre mot de passe a été réinitialisé. Vous pouvez maintenant vous connecter.
            </p>
            <div className="button-group">
              <button className="btn btn--primary" onClick={() => navigate('/connexion')}>
                Se connecter
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
